import type { DocumentV1 } from '@alpod-ai-writer/backend';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { useApi } from '../../api/useApi.hook';
import { RouteIndex } from '../../app/routeIndex';
import { pathWithParams } from '../../lib/routing.utils';
import { getSectionLink, type SectionAnchor } from '../hooks/useSectionNav.hook';
import { formatDateTime } from '../utils/string.utils';


export interface DocumentRevisionListProps {
    document: DocumentV1.Record;
    currentRevisionId: string;
    currentSection: SectionAnchor|null;
    className?: string;
}

export const DocumentRevisionList: React.FC<DocumentRevisionListProps> = ({
    document,
    currentRevisionId,
    currentSection,
    className,
}) => {
    const api = useApi();
    const nav = useNavigate();

    const { data, isLoading } = useQuery({
        queryKey: ['document', document.documentId, 'revisions'],
        queryFn: async () => {
            return api.v1.documents.getRevisionList(document.documentId, {
                sort: [ [ 'createdAt', 'desc' ] ]
            });
        }
    });

    const handleSelectRevision = useCallback((revisionId: string) => {
        location.href = pathWithParams(RouteIndex.documentDetail.editRevision, {
            documentId: document.documentId,
            revisionId
        }) + (
            currentSection
                ? getSectionLink(currentSection.sectionId, currentSection.headingId)
                : ''
        );
    }, [ document, nav, currentSection ]);

    return (
        <nav className={clsx(
            'flex flex-col gap-1.5 list-none w-full',
            className
        )}>
            {isLoading && (
                <>
                    <li className='w-full flex flex-row items-center justify-start text-left gap-1 text-sm py-2 px-4 rounded-full bg-gray-200 h-8'></li>
                    <li className='w-full flex flex-row items-center justify-start text-left gap-1 text-sm py-2 px-4 rounded-full bg-gray-200 h-8'></li>
                    <li className='w-full flex flex-row items-center justify-start text-left gap-1 text-sm py-2 px-4 rounded-full bg-gray-200 h-8'></li>
                </>
            )}

            {data?.items.map((revisions) => {
                const isSelected = revisions.revisionId === currentRevisionId;
                const title = formatDateTime(new Date(revisions.createdAt));

                return (
                    <li key={revisions.revisionId}>
                        <button
                            className={clsx(
                                'w-full flex flex-col text-left gap-1.5 text-sm py-2 px-4 rounded-3xl transition-colors duration-100 cursor-pointer',
                                (isSelected
                                    ? 'bg-blue-200 hover:bg-blue-300'
                                    : 'hover:bg-gray-200'
                                )
                            )}
                            onClick={() => handleSelectRevision(revisions.revisionId)}
                            aria-label={title}
                        >
                            <span className='flex flex-row items-center gap-2'>
                                <span className='block font-medium'>{title}</span>
                                {revisions.isCurrent && (
                                    <>
                                        <span className='text-xs font-medium bg-blue-500 rounded-full py-0.5 px-1.5 text-white'>Hlavní revize</span>
                                    </>
                                )}
                            </span>
                            {revisions.label && (
                                <span className='block text-gray-500 text-xs italic font-medium'>
                                    {revisions.label}
                                </span>
                            )}
                        </button>
                    </li>
                );
            })}
        </nav>
    );
};
